import { useState, type FormEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { Alert, Button, FormField, Input } from '@playerbase/ui';
import { ForgotPasswordFormSchema } from '@playerbase/types';

import { supabase } from '../lib/supabase.js';
import { getSiteUrl } from '../lib/site-url.js';
import { useSharedEmail } from '../lib/use-shared-email.js';
import { AuthShell } from './auth-shell.js';

export function ForgotPasswordPage(): JSX.Element {
  const location = useLocation();
  const flash = (location.state as { flash?: string } | null)?.flash;
  const [email, setEmail] = useSharedEmail();
  const [emailError, setEmailError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setEmailError('');

    const result = ForgotPasswordFormSchema.safeParse({ email });
    if (!result.success) {
      const issue = result.error.issues[0];
      if (issue) setEmailError(issue.message);
      return;
    }

    setSubmitting(true);
    try {
      // Always show a generic confirmation regardless of outcome so we do not
      // reveal whether an account exists for the given address.
      await supabase.auth.resetPasswordForEmail(result.data.email, {
        redirectTo: `${getSiteUrl()}/auth/callback?type=recovery`,
      });
    } finally {
      setSubmitting(false);
      setSent(true);
    }
  }

  if (sent) {
    return (
      <AuthShell title="Check your inbox" subtitle="We've sent a password reset link if that address is registered.">
        <div className="flex flex-col gap-4">
          <Alert tone="info">
            If an account exists for that address, we&apos;ve sent a reset link. Check your spam folder if you don&apos;t see it within a few minutes.
          </Alert>
          <p className="text-center text-sm text-text-muted">
            <Link to="/sign-in" className="text-accent underline underline-offset-2 hover:opacity-80 transition-opacity">
              Back to log in
            </Link>
          </p>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Reset your password" subtitle="Enter your email and we'll send you a reset link.">
      <div className="flex flex-col gap-6">
        {flash === 'reset-link-different-browser' && (
          <Alert tone="warning">
            Reset links must be opened in the browser you requested them from. Enter your email below to send a fresh link.
          </Alert>
        )}

        <form onSubmit={(e) => { void handleSubmit(e); }} className="flex flex-col gap-4" noValidate>
          <FormField error={emailError !== '' ? emailError : undefined}>
            <FormField.Label>Email</FormField.Label>
            <FormField.Control>
              <Input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full"
              />
            </FormField.Control>
            {emailError !== '' && <FormField.ErrorText>{emailError}</FormField.ErrorText>}
          </FormField>

          <Button type="submit" variant="primary" className="w-full" disabled={submitting}>
            {submitting ? 'Sending…' : 'Send reset link'}
          </Button>
        </form>

        <p className="text-center text-sm text-text-muted">
          Remembered?{' '}
          <Link to="/sign-in" className="text-accent underline underline-offset-2 hover:opacity-80 transition-opacity">
            Log in
          </Link>
        </p>
      </div>
    </AuthShell>
  );
}
